// Admin Panel - Porters Management Page
// File: admin-panel/src/pages/Porters.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, addDoc, updateDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase'
import {
  UsersIcon,
  PlusIcon,
  PencilIcon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  TruckIcon,
  BanknotesIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

function PorterModal({ isOpen, onClose, porter, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    employeeId: '',
    password: '',
    monthlySalary: '',
    vehicleType: 'Two Wheeler',
    vehicleNumber: '',
    assignedZone: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (porter) {
      setFormData({
        name: porter.name || '',
        phone: porter.phone || '',
        email: porter.email || '',
        employeeId: porter.employeeId || '',
        password: '',
        monthlySalary: porter.monthlySalary?.toString() || '',
        vehicleType: porter.vehicle?.type || 'Two Wheeler',
        vehicleNumber: porter.vehicle?.number || '',
        assignedZone: porter.assignedZone || '',
        isActive: porter.isActive !== false,
      });
    } else {
      setFormData({
        name: '',
        phone: '',
        email: '',
        employeeId: `EMP${Date.now().toString().slice(-6)}`,
        password: '',
        monthlySalary: '25000',
        vehicleType: 'Two Wheeler',
        vehicleNumber: '',
        assignedZone: '',
        isActive: true,
      });
    }
  }, [porter, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const porterData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        employeeId: formData.employeeId.trim(),
        monthlySalary: parseFloat(formData.monthlySalary) || 0,
        salary: parseFloat(formData.monthlySalary) || 0,
        vehicle: {
          type: formData.vehicleType,
          number: formData.vehicleNumber.trim().toUpperCase(),
        },
        assignedZone: formData.assignedZone.trim(),
        isActive: formData.isActive,
        isOnline: false,
        updatedAt: new Date().toISOString(),
      };

      if (formData.password) {
        porterData.password = formData.password;
      }

      if (!porter) {
        porterData.stats = {
          completedDeliveries: 0,
          rating: 5.0,
          onTimePercentage: 100,
          totalCollections: 0,
          pendingDeposits: 0,
        };
        porterData.attendance = {
          presentDays: 0,
          totalDays: 0,
          leaveBalance: 12,
        };
        porterData.createdAt = new Date().toISOString();
        porterData.password = formData.password || '123456';
      }

      await onSave(porterData);
      onClose();
    } catch (error) {
      console.error('Save porter error:', error);
      alert('Failed to save porter');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">{porter ? 'Edit Porter' : 'Add New Porter'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-gray-700 text-sm font-medium mb-2">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 98765 43210"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Employee ID</label>
              <input
                type="text"
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">{porter ? 'New Password' : 'Password *'}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={porter ? 'Leave blank to keep current' : 'Set password'}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required={!porter}
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Monthly Salary (₹)</label>
              <input
                type="number"
                value={formData.monthlySalary}
                onChange={(e) => setFormData({ ...formData, monthlySalary: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Assigned Zone</label>
              <input
                type="text"
                value={formData.assignedZone}
                onChange={(e) => setFormData({ ...formData, assignedZone: e.target.value })}
                placeholder="e.g., Chennai Central"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Vehicle Type</label>
              <select
                value={formData.vehicleType}
                onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="Two Wheeler">Two Wheeler</option>
                <option value="Three Wheeler">Three Wheeler</option>
                <option value="Four Wheeler">Four Wheeler</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Vehicle Number</label>
              <input
                type="text"
                value={formData.vehicleNumber}
                onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                placeholder="TN 01 AB 1234"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 uppercase"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <label htmlFor="isActive" className="text-gray-900 font-medium">Porter is Active</label>
          </div>

          <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl disabled:opacity-50 transition-colors">
              {loading ? 'Saving...' : (porter ? 'Update' : 'Add Porter')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PorterCard({ porter, onEdit }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl font-bold">{porter.name?.charAt(0)}</span>
            </div>
            <div>
              <h3 className="text-gray-900 font-semibold text-lg">{porter.name}</h3>
              <p className="text-gray-500 text-sm">{porter.employeeId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${porter.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span className={`text-sm font-medium ${porter.isOnline ? 'text-green-600' : 'text-gray-500'}`}>
              {porter.isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3 text-gray-700">
            <PhoneIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm">{porter.phone}</span>
          </div>
          {porter.email && (
            <div className="flex items-center gap-3 text-gray-700">
              <EnvelopeIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm truncate">{porter.email}</span>
            </div>
          )}
          {porter.assignedZone && (
            <div className="flex items-center gap-3 text-gray-700">
              <MapPinIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm">{porter.assignedZone}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-gray-700">
            <TruckIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm">{porter.vehicle?.type} - {porter.vehicle?.number}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 py-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{porter.stats?.completedDeliveries || 0}</p>
            <p className="text-xs text-gray-500">Deliveries</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <StarIcon className="w-4 h-4 text-yellow-500" />
              <p className="text-2xl font-bold text-gray-900">{porter.stats?.rating?.toFixed(1) || '5.0'}</p>
            </div>
            <p className="text-xs text-gray-500">Rating</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{porter.stats?.onTimePercentage || 100}%</p>
            <p className="text-xs text-gray-500">On-Time</p>
          </div>
        </div>

        {porter.stats?.pendingDeposits > 0 && (
          <div className="flex items-center justify-between py-3 px-4 bg-red-50 border border-red-200 rounded-xl mb-4">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="w-5 h-5 text-red-600" />
              <span className="text-red-700 font-medium">Cash Pending</span>
            </div>
            <span className="text-red-700 font-bold">₹{porter.stats.pendingDeposits.toLocaleString('en-IN')}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(porter)}
            className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
            Edit
          </button>
          <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
            porter.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {porter.isActive ? 'Active' : 'Inactive'}
          </div>
        </div>
      </div>
    </div>
  );
}

function Porters() {
  const [porters, setPorters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPorter, setEditPorter] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const portersQuery = query(collection(db, 'porters'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(portersQuery, (snapshot) => {
      const portersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPorters(portersList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSavePorter = async (porterData) => {
    if (editPorter) {
      await updateDoc(doc(db, 'porters', editPorter.id), porterData);
    } else {
      await addDoc(collection(db, 'porters'), porterData);
    }
    setEditPorter(null);
  };

  const handleEditPorter = (porter) => {
    setEditPorter(porter);
    setShowModal(true);
  };

  const filteredPorters = porters.filter(porter => {
    if (filter === 'online') return porter.isOnline;
    if (filter === 'offline') return !porter.isOnline;
    if (filter === 'active') return porter.isActive;
    if (filter === 'inactive') return !porter.isActive;
    return true;
  });

  const onlineCount = porters.filter(p => p.isOnline).length;
  const activeCount = porters.filter(p => p.isActive).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Porters</h1>
          <p className="text-gray-500 mt-1">Manage delivery personnel</p>
        </div>
        <button
          onClick={() => { setEditPorter(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl shadow-lg shadow-red-600/20 transition-all"
        >
          <PlusIcon className="w-5 h-5" />
          Add Porter
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Total</p>
          <p className="text-2xl font-bold text-gray-900">{porters.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Online</p>
          <p className="text-2xl font-bold text-green-600">{onlineCount}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Active</p>
          <p className="text-2xl font-bold text-blue-600">{activeCount}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Offline</p>
          <p className="text-2xl font-bold text-gray-500">{porters.length - onlineCount}</p>
        </div>
      </div>

      <div className="flex gap-2">
        {[
          { value: 'all', label: 'All' },
          { value: 'online', label: 'Online' },
          { value: 'offline', label: 'Offline' },
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ].map(opt => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === opt.value 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {filteredPorters.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPorters.map(porter => (
            <PorterCard key={porter.id} porter={porter} onEdit={handleEditPorter} />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center shadow-sm">
          <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Porters Found</h3>
          <p className="text-gray-500 mb-6">
            {filter !== 'all' ? 'Try adjusting your filter' : 'Add your first porter to get started'}
          </p>
          <button
            onClick={() => { setEditPorter(null); setShowModal(true); }}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors"
          >
            Add Porter
          </button>
        </div>
      )}

      <PorterModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditPorter(null); }}
        porter={editPorter}
        onSave={handleSavePorter}
      />
    </div>
  );
}

export default Porters;